/**
 * The widget's time module.
 *
 * Same rule as the API's and the admin app's: one file per app that touches
 * dates. Exempt from `check:time` for that reason and no other.
 *
 * Everything is rendered in the VENUE's timezone. A guest booking from London
 * for a table in Doha must see Doha's evening, not their own.
 */

export interface VenueTime {
  timezone: string;
  serviceDayCutoffHour: number;
  digitStyle?: 'western' | 'arabic';
}

export function now(): Date {
  return new Date();
}

function parts(instant: Date, venue: VenueTime) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: venue.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const bag: Record<string, string> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== 'literal') bag[part.type] = part.value;
  }
  const hour = bag.hour === '24' ? '00' : bag.hour;
  return { date: `${bag.year}-${bag.month}-${bag.day}`, hour: Number(hour) };
}

export function addServiceDays(date: string, days: number): string {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  utc.setUTCDate(utc.getUTCDate() + days);
  return utc.toISOString().slice(0, 10);
}

/**
 * The service date an instant belongs to.
 *
 * Before the venue's cutoff hour an instant belongs to the PREVIOUS service
 * date, so a guest opening the widget at 01:00 during Ramadan is offered
 * tonight's remaining suhoor slots rather than tomorrow's.
 */
export function serviceDateFor(instant: Date, venue: VenueTime): string {
  const p = parts(instant, venue);
  return p.hour < venue.serviceDayCutoffHour ? addServiceDays(p.date, -1) : p.date;
}

export function currentServiceDate(venue: VenueTime): string {
  return serviceDateFor(now(), venue);
}

/** Pin the numbering system in both directions - never trust the locale default. */
function intlLocale(locale: string, digitStyle: 'western' | 'arabic'): string {
  const numbering = digitStyle === 'arabic' ? 'arab' : 'latn';
  return locale.includes('-u-') ? `${locale}-nu-${numbering}` : `${locale}-u-nu-${numbering}`;
}

function asDate(value: Date | string): Date {
  if (typeof value !== 'string') return value;
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value);
}

export function formatTime(value: Date | string, venue: VenueTime, locale = 'en'): string {
  return new Intl.DateTimeFormat(intlLocale(locale, venue.digitStyle ?? 'western'), {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: venue.timezone,
  }).format(asDate(value));
}

export function formatDate(value: Date | string, venue: VenueTime, locale = 'en'): string {
  return new Intl.DateTimeFormat(intlLocale(locale, venue.digitStyle ?? 'western'), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: venue.timezone,
  }).format(asDate(value));
}

/**
 * Hijri, Umm al-Qura - the civil calendar in the Gulf.
 *
 * Shown beside every Gregorian date in the picker. During Ramadan a guest
 * thinks in Hijri, and a widget that only speaks Gregorian makes them count.
 */
export function formatHijri(value: Date | string, venue: VenueTime, locale = 'ar'): string {
  return new Intl.DateTimeFormat(
    intlLocale(`${locale}-u-ca-islamic-umalqura`, venue.digitStyle ?? 'western'),
    { day: 'numeric', month: 'long', timeZone: venue.timezone },
  ).format(asDate(value));
}

/** Short weekday plus day number, for the date strip. */
export function formatDayChip(
  value: string,
  venue: VenueTime,
  locale = 'en',
): { weekday: string; day: string } {
  const date = asDate(value);
  const style = venue.digitStyle ?? 'western';
  return {
    weekday: new Intl.DateTimeFormat(intlLocale(locale, style), {
      weekday: 'short',
      timeZone: venue.timezone,
    }).format(date),
    day: new Intl.DateTimeFormat(intlLocale(locale, style), {
      day: 'numeric',
      timeZone: venue.timezone,
    }).format(date),
  };
}
